<template
>
  <div class="page">
    <van-nav-bar
      title="日志复核"
      left-arrow
      @click-left="$router.back()"
    />
    <van-skeleton v-if="loading" title :row="10"/>
    <template
      v-else-if="detail"
    >
      <header class="employee">
        <div class="avatar">{{ detail.employeeName.slice(-1) }}</div>
        <div>
          <h1>{{ detail.employeeName }}</h1>
          <p>
            {{ detail.positionName }} · {{ detail.workDate }}
            {{ detail.cadence === "MONTHLY" ? "周报" : "日报" }}
          </p>
        </div>
      </header>
      <main>
        <h2>日志内容</h2>
        <section v-for="(value, key) in detail.fieldValues" :key="key">
          <label>{{ fieldLabel(key) }}</label>
          <p>{{ key === 'dailyLogs' ? formatDailyLogs(value) : (key === 'monthlyAchievements' ? formatWeeklyLogs(value) : (value || "未填写")) }}</p>
        </section>
        <van-empty
          v-if="!Object.keys(detail.fieldValues || {}).length"
          description="暂无结构化日志内容"
        />
      </main>
      <section class="score-meaning">
        <div>
          <span>工作方向分</span><b>{{
            directionAssessment && directionAssessment.score != null ? Number(directionAssessment.score).toFixed(1) : '--'
          }}</b><small>判断工作是否符合战略、项目和任务方向，不计入月度绩效</small>
        </div>
        <div>
          <span>工作质量分</span><b>{{
            detail.aiAssessment && detail.aiAssessment.suggestedScore != null ? Number(detail.aiAssessment.suggestedScore).toFixed(1) : '--'
          }}</b><small>判断完成质量和证据充分性，辅助月度绩效复核</small>
        </div>
      </section>
      <section v-if="directionAssessment" class="direction">
        <header>
          <div>
            <h2>工作方向匹配</h2>
            <span>{{ directionSourceText(directionAssessment.sourceType) }} · 不计入月度绩效</span>
          </div>
          <b>{{ directionAssessment.score == null ? "--" : Number(directionAssessment.score).toFixed(1) }}</b>
        </header>
        <div
          v-if="directionAssessment.traceCoverage != null || directionAssessment.sourceType === 'AUTONOMOUS' || directionAssessment.status === 'PENDING'"
          class="direction-summary"
        >
          <div v-if="directionAssessment.traceCoverage != null" class="trace-tip">
            <span class="trace-label">战略承接链覆盖</span>
            <strong class="trace-value">{{ Number(directionAssessment.traceCoverage).toFixed(0) }}<em>%</em></strong>
          </div>
          <p v-if="directionAssessment.sourceType === 'AUTONOMOUS'" class="autonomous-tip">
            当前暂无已绑定的战略、项目或任务，本次按<strong>自主工作路径</strong>评估；后续建立承接关系后可补充关联。
          </p>
          <p v-if="directionAssessment.status === 'PENDING'" class="pending-tip">方向分析生成中，可稍后刷新。</p>
        </div>
        <article v-for="item in directionAssessment.dimensions || []" :key="item.code" v-if="item.applicable">
          <div><b>{{ directionDimensionName(item.code) }}</b><span>{{ item.reason }}</span></div>
          <strong>{{ item.score == null ? "--" : Number(item.score).toFixed(0) }}</strong>
        </article>
        <div v-if="(directionAssessment.deviations || []).length" class="direction-list deviation">
          <h3>发现的偏差</h3>
          <p v-for="(item, index) in directionAssessment.deviations" :key="'d' + index">{{ item.description || item.type }}</p>
        </div>
        <div v-if="(directionAssessment.suggestions || []).length" class="direction-list suggestion">
          <h3>下一步建议</h3>
          <p v-for="(item, index) in directionAssessment.suggestions" :key="'s' + index">{{ item.action }}
            <template v-if="item.expectedOutcome">：{{ item.expectedOutcome }}</template>
          </p>
        </div>
      </section>
      <section
        v-if="detail.aiAssessment && detail.aiAssessment.applicable"
        class="ai"
      >
        <header>
          <div>
            <h2>AI 评估</h2>
            <span>{{ statusText(detail.aiAssessment.status) }}</span>
          </div>
          <b>{{
              detail.aiAssessment.qualitativeScore == null
                ? "--"
                : Number(detail.aiAssessment.qualitativeScore).toFixed(1)
            }}</b>
        </header>
        <p v-if="aiPending" class="pending-tip">
          员工日志已可复核；AI 建议分生成中，可稍后刷新查看。
          <button type="button" class="refresh-ai" :disabled="refreshing" @click="refreshAi">
            {{ refreshing ? "刷新中" : "刷新 AI 结果" }}
          </button>
        </p>
        <p class="composition">
          <template v-if="!isFinance">
            绩效 {{ num(detail.aiAssessment.kpiScore) }} ·
          </template>
          AI 非量化
          {{
            detail.aiAssessment.qualitativeScore == null
              ? "--"
              : num(detail.aiAssessment.qualitativeScore)
          }}
          · 建议 {{ num(detail.aiAssessment.suggestedScore) }}
        </p>
        <p v-if="detail.aiAssessment.fallbackReason" class="fallback">
          {{ detail.aiAssessment.fallbackReason }}
        </p>
        <article
          v-for="item in detail.aiAssessment.dimensions || []"
          :key="item.code"
        >
          <div>
            <b>{{ dimensionName(item) }}</b
            ><span>{{ reasonText(item.reason) }}</span>
          </div>
          <strong>{{ item.score }}</strong>
        </article>
        <small
        >AI 仅提供复核建议；员工自述未经独立核验时不能进入 70
          分以上，最终分由主管确认。</small
        >
      </section>
      <section class="score">
        <div>
          <h2>{{ isFinance ? "老板满意度评分" : "主管复核" }}</h2>
          <span>{{
              isFinance
                ? "老板直接评分"
                : "系统建议分 " + Number(detail.initialScore || 0).toFixed(1)
            }}</span>
        </div>
        <p v-if="isFinance" class="finance-tip">
          AI 评分仅作为老板满意度评价参考，不自动替代老板评分。50 分对应绩效工资不调整，当前评分对应
          <b>{{ salaryAdjustment }}</b
          >。
        </p>
        <div v-if="isFinance" class="finance-slider">
          <div class="slider-value">
            <span>满意度</span>
          </div>
          <van-slider
            v-model="sliderScore"
            :min="0"
            :max="100"
            :step="1"
            :bar-height="6"
            active-color="#B71C1C"
            inactive-color="#e7e8f2"
          >
            <div slot="button" class="slider-button">{{ sliderScore }}</div>
          </van-slider>
          <div class="slider-marks">
            <span>-5%</span><span>+5%</span>
          </div>
        </div>
        <van-field
          v-else
          v-model="finalScore"
          type="number"
          label="最终评分"
          placeholder="0-100"
          input-align="right"
          @input="scoreTouched = true"
        ><span slot="right-icon">分</span></van-field
        >
        <van-field
          v-model="reason"
          type="textarea"
          rows="3"
          maxlength="500"
          show-word-limit
          required
          label="复核理由"
          placeholder="请填写复核理由"
        />
      </section>
      <section class="score">
        <div class="star-rank">
          <div class="star-rank-title">
            <span>五星上将排序</span>
            <em>可拖动排序</em>
          </div>
          <div ref="starList" class="star-rank-list">
            <div
              v-for="(dim, index) in starRank"
              :key="index"
              class="star-rank-item"
              :class="{ dragging: dragIndex === index }"
              draggable="true"
              @dragstart="onDragStart(index)"
              @dragover.prevent="onDragOver(index)"
              @drop="onDrop(index)"
              @dragend="onDragEnd"
              @touchstart="onTouchStart(index, $event)"
              @touchmove="onTouchMove($event)"
              @touchend="onTouchEnd($event)"
            >
              <div class="star-btn" :class="{ empty: !dim }" @click="openStarPicker(index)">
                <span class="star-dim">{{ dim || "请选择" }}</span>
                <i class="star-arrow"/>
              </div>
              <span class="star-order">{{ index + 1 }}</span>
            </div>
          </div>
          <p class="star-rank-tip">注：请根据员工实际表现依次选择对应项</p>
          <p class="star-rank-result">得分：{{ starScore }}</p>
          <p class="star-rank-formula" v-if="starFormula">计算式：{{ starFormula }}</p>
        </div>
      </section>
      <van-action-sheet v-model="showStarPicker" :actions="pickerActions" cancel-text="取消" @select="onSelectDimension"
                        @cancel="showStarPicker = false"/>
      <footer>
        <van-button
          class="review-action"
          plain
          color="#d85b5b"
          :loading="saving"
          @click="submit('RETURN')"
        >驳回整改
        </van-button
        >
        <van-button class="review-action primary" color="#B71C1C" :loading="saving" @click="submit('APPROVE')">{{
            isFinance ? "确认评分" : "通过"
          }}
        </van-button>
      </footer>
    </template
    >
  </div>
</template
>
<script>
/**
 * 主管单条日志复核：展示 AI 辅助评分、财务岗老板满意度滑块、通过/调整/驳回。
 * AI_PENDING 时 4 秒轮询静默刷新，评分完成后可自动填入建议分。
 */
import coreapi from "@/utils/coreapi";
import {worklogFieldLabel} from "@/utils/worklogFields";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      refreshing: false,
      detail: null,
      directionAssessment: null,
      finalScore: "",
      reason: "",
      pollTimer: null,
      scoreTouched: false,
      starDimensions: ["会算账", "有方法", "讲效能", "带团队", "拿结果"],
      starRank: ["", "", "", "", ""],
      showStarPicker: false,
      pickerSlotIndex: null,
      dragIndex: null,
      touchStartIndex: null,
      touchStartY: null
    };
  },
  computed: {
    // 财务岗由 Boss 直接打满意度分，AI 仅作参考
    isFinance() {
      return (
        this.detail &&
        (this.detail.positionFamily === "FINANCE" ||
          ["财务", "总经办财务岗"].includes(this.detail.positionName))
      );
    },
    aiPending() {
      return (
        this.detail &&
        this.detail.aiAssessment &&
        this.detail.aiAssessment.status === "AI_PENDING"
      );
    },
    // 50 分 = 绩效工资不调整，每 10 分对应 ±1%
    sliderScore: {
      get() {
        return Math.round(
          Math.max(0, Math.min(100, Number(this.finalScore) || 0))
        );
      },
      set(value) {
        this.scoreTouched = true;
        this.finalScore = String(value);
      }
    },
    salaryAdjustment() {
      const score = Math.max(0, Math.min(100, Number(this.finalScore) || 0));
      const value = (score - 50) / 10;
      return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
    },
    pickerActions() {
      return this.starDimensions.map((name) => {
        const usedAt = this.starRank.indexOf(name);
        return {name, value: name, disabled: usedAt !== -1 && usedAt !== this.pickerSlotIndex};
      });
    },
    starScore() {
      const weights = {1: 1, 2: 0.9, 3: 0.8, 4: 0.7, 5: 0.6};
      const baseScores = {"会算账": 1, "有方法": 2, "讲效能": 2, "带团队": 2, "拿结果": 3};
      const filled = this.starRank.filter((d) => d).length;
      if (filled < 5) return "0.0";
      const result = this.starRank.reduce((sum, dim, i) => {
        const w = weights[i + 1] || 0;
        return sum + (baseScores[dim] || 0) * w;
      }, 0);
      return result.toFixed(1);
    },
    starFormula() {
      const weights = {1: 1, 2: 0.9, 3: 0.8, 4: 0.7, 5: 0.6};
      const baseScores = {"会算账": 1, "有方法": 2, "讲效能": 2, "带团队": 2, "拿结果": 3};
      const filled = this.starRank.filter((d) => d).length;
      if (filled < 5) return "";
      const parts = this.starRank.map((dim, i) => {
        const base = baseScores[dim] || 0;
        const w = weights[i + 1] || 0;
        return `${base}*${w}`;
      });
      return `${this.starScore}=${parts.join("+")}`;
    }
  },
  watch: {
    // AI 异步评分中：进入轮询，完成后 stopPolling
    aiPending: {
      immediate: true,
      handler(pending) {
        this.stopPolling();
        if (pending) this.startPolling();
      }
    }
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    num(v) {
      return Number(v || 0).toFixed(1);
    },
    fieldLabel(v) {
      return worklogFieldLabel(v);
    },
    formatDailyLogs(value) {
      if (!value) return '未填写';
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const today = (parsed.today || []).map((v, i) => '今日 ' + (i + 1) + '. ' + (v || '未填写')).join('\n');
          const tomorrow = (parsed.tomorrow || []).map((v, i) => '明日 ' + (i + 1) + '. ' + (v || '未填写')).join('\n');
          return [today, tomorrow].filter(Boolean).join('\n');
        }
        if (Array.isArray(parsed)) {
          return parsed.map((v, i) => (i + 1) + '. ' + (v || '未填写')).join('\n');
        }
      } catch (e) {
      }
      return value;
    },
    formatWeeklyLogs(value) {
      if (!value) return '未填写';
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const thisWeek = (parsed.thisWeek || []).map((v, i) => '本周 ' + (i + 1) + '. ' + (v || '未填写')).join('\n');
          const nextWeek = (parsed.nextWeek || []).map((v, i) => '下周 ' + (i + 1) + '. ' + (v || '未填写')).join('\n');
          return [thisWeek, nextWeek].filter(Boolean).join('\n');
        }
        if (Array.isArray(parsed)) {
          return parsed.map((v, i) => (i + 1) + '. ' + (v || '未填写')).join('\n');
        }
      } catch (e) {
      }
      return value;
    },
    statusText(v) {
      return (
        {
          AI_PENDING: "AI 评分中",
          DEGRADED_RULE: "规则门禁评估",
          AI_SCORED: "AI 已评估",
          DEGRADED_UNAVAILABLE: "AI 不可用"
        }[v] || v
      );
    },
    dimensionName(dimension) {
      if (dimension && dimension.name) return dimension.name;
      const v = dimension && dimension.code ? dimension.code : dimension;
      return (
        {
          metric_relevance: "量化指标相关性",
          work_fact_completeness: "工作事实完整度",
          result_data_clarity: "结果数据清晰度",
          execution_depth: "执行深度",
          evidence_traceability: "证据可追溯性",
          sales_process: "销售过程质量",
          channel_quality: "渠道开发质量",
          meeting_reception: "会务接待",
          payment_review: "收支与报销审核",
          accounting_compliance: "财务税务与合规",
          reconciliation_assets: "对账与资产管理",
          reporting_support: "销售收款汇报与经营支持",
          reception_service: "接待服务",
          environment_service: "环境与会务保障",
          logistics_execution: "后勤执行与响应",
          recruitment_process: "招聘过程与到岗转化",
          retention_management: "员工留存跟进与风险处置",
          personnel_compliance: "人事手续与协同质量",
          market_user_research: "市场调研、用户需求与产品规划",
          project_delivery: "项目全流程进度、质量与风险管理",
          testing_acceptance: "产品测试、功能校验与上线验收",
          ai_innovation: "AI与大模型场景融合及创新落地",
          cross_department_delivery: "跨部门协同、上线与常态化运营",
          requirement_solution_delivery: "需求分析、技术方案与按期交付",
          full_stack_implementation: "前后端功能开发、接口联调与代码质量",
          testing_release_quality: "测试、缺陷修复、发布与验收质量",
          stability_performance_security: "部署运维、稳定性、性能、数据与安全",
          collaboration_documentation: "跨部门协同、技术文档与知识沉淀",
          ui_interface_design: "Web、H5、小程序及后台UI界面设计",
          interaction_visual_solution: "需求讨论、交互与视觉方案",
          prototype_high_fidelity: "产品原型与高保真设计稿转化",
          implementation_acceptance: "前端还原配合与视觉验收",
          design_system: "UI规范、组件、色彩与字体体系",
          ux_optimization: "基于数据反馈的用户体验优化",
          graphic_vi_material: "海报、VI及宣传物料设计"
        }[v] || v
      );
    },
    directionSourceText(value) {
      return ({BOSS_TASK: "Boss 下发", PROJECT_TASK: "项目任务", SUPERVISOR_TASK: "主管安排", AUTONOMOUS: "自主工作"})[value] || "待识别";
    },
    directionDimensionName(value) {
      return ({
        boss_request: "Boss 要求",
        company_strategy: "公司战略",
        business_unit_strategy: "事业部战略",
        business_unit_okr: "事业部目标（OKR）",
        project_goal: "项目目标",
        supervisor_task: "主管任务",
        strategic_necessity: "战略必要性",
        business_value: "业务价值",
        outcome_progress: "成果推进",
        position_positioning: "岗位定位",
        position_responsibility: "岗位职责"
      })[value] || value;
    },
    reasonText(v) {
      const translations = {
        "No assessable work fact was provided": "未提供可评估的工作事实",
        "Description or responsibility restatement is not evidence of completed work":
          "仅描述计划、岗位职责或笼统内容，不能证明工作已经完成",
        "Description or responsibility restatement is not evidence of completed work; responsibility keyword overlap gives no credit":
          "仅描述计划、岗位职责或笼统内容，不能证明工作已经完成；与岗位职责关键词重合不作为加分依据",
        "Work is claimed but lacks a concrete object, quantity, date, or traceable result":
          "虽描述了工作动作，但缺少具体对象、数量、日期或可追溯结果",
        "A concrete object and action exist, but no observable result is stated":
          "已说明具体工作对象和动作，但未说明可观察的完成结果",
        "A traceable object and result are stated, but independent system verification is still required":
          "已说明可追溯的工作对象和结果，但仍需通过独立系统记录核验"
      };
      const source = v || "";
      const english = Object.keys(translations).find(text =>
        source.startsWith(text)
      );
      return english
        ? `${translations[english]}${source.slice(english.length)}`
        : source;
    },
    /** silent 模式用于轮询/手动刷新，不展示全页 skeleton */
    async load(options = {}) {
      const silent = !!options.silent;
      if (!silent) this.loading = true;
      try {
        const previousStatus =
          this.detail && this.detail.aiAssessment
            ? this.detail.aiAssessment.status
            : null;
        this.detail = await coreapi.supervisorReviewDetail(
          this.$route.params.id
        );
        try {
          const direction = await coreapi.worklogDirectionAssessment(this.detail.worklogId);
          this.directionAssessment = direction && direction.status !== "NOT_ASSESSED" ? direction : null;
        } catch (ignore) {
          this.directionAssessment = null;
        }
        const r = this.detail.review;
        if (r) {
          this.finalScore = String(r.finalScore);
          this.reason = r.reason || "";
          this.scoreTouched = true;
          try {
            if (r.starRank) {
              const parsed = JSON.parse(r.starRank);
              if (Array.isArray(parsed) && parsed.length === 5) this.starRank = parsed;
              else if (Array.isArray(parsed)) this.starRank = parsed.concat(["", "", "", "", ""]).slice(0, 5);
            }
          } catch (e) {
          }
        } else if (!this.scoreTouched || !silent) {
          this.finalScore = String(this.detail.initialScore || 0);
        }
        if (
          silent &&
          previousStatus === "AI_PENDING" &&
          this.detail.aiAssessment &&
          this.detail.aiAssessment.status === "AI_SCORED"
        ) {
          // 主管未手动改分且尚无复核记录时，采用 AI 建议分
          if (!this.scoreTouched && !r) {
            this.finalScore = String(
              this.detail.aiAssessment.suggestedScore ||
              this.detail.initialScore ||
              0
            );
          }
          this.$toast.success("AI 评分已更新");
        }
      } catch (e) {
        if (!silent) this.$toast.fail(e.message);
      } finally {
        if (!silent) this.loading = false;
      }
    },
    async refreshAi() {
      if (this.refreshing) return;
      this.refreshing = true;
      try {
        await this.load({silent: true});
        if (this.aiPending) this.$toast("AI 仍在评分中，请稍后再试");
      } finally {
        this.refreshing = false;
      }
    },
    startPolling() {
      this.stopPolling();
      // 4 秒一次静默拉取，避免与保存/手动刷新并发
      this.pollTimer = setInterval(() => {
        if (!this.aiPending || this.refreshing || this.saving) return;
        this.load({silent: true});
      }, 4000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async submit(action) {
      const score = Number(this.finalScore);
      if (!Number.isFinite(score) || score < 0 || score > 100)
        return this.$toast("评分必须在 0 至 100 之间");
      if (!this.reason.trim())
        return this.$toast("请填写复核理由");
      if (this.starRank.some((d) => !d))
        return this.$toast("请完成五星上将排序");
      this.saving = true;
      try {
        const starRankArr = this.starRank.map((d) => d || "");
        await coreapi.submitSupervisorReview(this.detail.worklogId, {
          action,
          finalScore: score,
          reason: this.reason,
          starRank: JSON.stringify(starRankArr),
          starScore: Number(this.starScore)
        });
        this.$toast.success(
          action === "RETURN" ? "已退回员工整改" : "复核已完成"
        );
        this.$router.replace({name: "SupervisorReviews"});
      } catch (e) {
        this.$toast.fail(e.message);
      } finally {
        this.saving = false;
      }
    },
    orderMark(index) {
      return ["①", "②", "③", "④", "⑤"][index] || "";
    },
    openStarPicker(index) {
      this.pickerSlotIndex = index;
      this.showStarPicker = true;
    },
    onSelectDimension(item) {
      if (this.pickerSlotIndex == null) return;
      this.$set(this.starRank, this.pickerSlotIndex, item.value);
      this.showStarPicker = false;
      this.pickerSlotIndex = null;
    },
    onDragStart(index) {
      this.dragIndex = index;
    },
    onDragOver(index) {
      if (this.dragIndex === null || this.dragIndex === index) return;
      const list = this.starRank.slice();
      const dragged = list[this.dragIndex];
      list.splice(this.dragIndex, 1);
      list.splice(index, 0, dragged);
      this.starRank = list;
      this.dragIndex = index;
    },
    onDrop(index) {
      this.dragIndex = null;
    },
    onDragEnd() {
      this.dragIndex = null;
    },
    onTouchStart(index, e) {
      this.touchStartIndex = index;
      this.touchStartY = e.touches[0].clientY;
    },
    onTouchMove(e) {
      if (this.touchStartIndex == null) return;
      e.preventDefault();
      const touch = e.touches[0];
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const item = el && el.closest(".star-rank-item");
      if (!item) return;
      const index = [...this.$refs.starList.children].indexOf(item);
      if (index !== -1 && index !== this.touchStartIndex) {
        const list = this.starRank.slice();
        const dragged = list[this.touchStartIndex];
        list.splice(this.touchStartIndex, 1);
        list.splice(index, 0, dragged);
        this.starRank = list;
        this.touchStartIndex = index;
      }
    },
    onTouchEnd() {
      this.touchStartIndex = null;
      this.touchStartY = null;
    }
  }
};
</script>
<style lang="less" scoped>
@import "~@/assets/base.less";
@import "./supervisor.less";

.page {
  min-height: 100vh;
  padding-bottom: 2.6rem;
  background: #f6f7fb;
}

.employee {
  padding: 0.34rem 0.3rem;
  display: flex;
  align-items: center;
  color: #fff;
  background: linear-gradient(135deg, #4a4a4a 0%, #1a1a1a 100%);
}

.avatar {
  width: 0.88rem;
  height: 0.88rem;
  line-height: 0.88rem;
  margin-right: 0.22rem;
  text-align: center;
  border-radius: 50%;
  color: #fff;
  background: @color;
  font-size: 0.32rem;
}

.employee h1 {
  font-size: 0.38rem;
}

.employee p {
  margin-top: 0.05rem;
  font-size: 0.26rem;
  opacity: 0.75;
}

main,
.score,
.ai,
.direction {
  margin: 0.25rem 0.28rem;
  padding: 0.3rem;
  background: #fff;
  border-radius: 0.16rem;
  box-shadow: 0 0.04rem 0.16rem rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

main::before,
.score::before,
.ai::before,
.direction::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.24rem;
  width: 0.06rem;
  height: 0.36rem;
  border-radius: 0 0.04rem 0.04rem 0;
  background: @color;
}

.score-meaning {
  margin: .25rem .28rem;
  padding: .24rem;
  display: grid;
  grid-template-columns:1fr 1fr;
  gap: .16rem;
  background: #fff;
  border-radius: .16rem
}

.score-meaning div {
  padding: .18rem;
  background: #f8f8fa;
  border-radius: .12rem
}

.score-meaning span, .score-meaning b, .score-meaning small {
  display: block
}

.score-meaning span {
  font-size: .25rem
}

.score-meaning b {
  margin: .06rem 0;
  color: #b71c1c;
  font-size: .42rem
}

.score-meaning small {
  color: #8b8d9d;
  font-size: .21rem;
  line-height: 1.4
}

.direction header,
.direction article {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.2rem;
}

.direction header span,
.direction article span {
  display: block;
  margin-top: 0.06rem;
  color: #8b8d9d;
  font-size: 0.24rem;
  line-height: 1.5;
}

.direction header > b {
  color: @color;
  font-size: 0.48rem;
}

.direction-summary {
  margin-top: 0.18rem;
  padding: 0.22rem 0.24rem;
  background: linear-gradient(180deg, #fafbfc 0%, #f6f7fb 100%);
  border: 1px solid #eceef3;
  border-radius: 0.12rem;
}

.trace-tip {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.16rem;
}

.trace-label {
  color: #5f6272;
  font-size: 0.26rem;
  line-height: 1.4;
}

.trace-value {
  color: @color;
  font-size: 0.42rem;
  font-weight: 600;
  line-height: 1;
}

.trace-value em {
  margin-left: 0.02rem;
  font-style: normal;
  font-size: 0.24rem;
  font-weight: 500;
}

.autonomous-tip {
  margin: 0.16rem 0 0;
  padding: 0.16rem 0.18rem 0.16rem 0.22rem;
  background: #fff;
  border-left: 0.06rem solid #e09a2d;
  border-radius: 0 0.08rem 0.08rem 0;
  color: #5f6272;
  font-size: 0.25rem;
  line-height: 1.65;
}

.autonomous-tip strong {
  color: #8a5a12;
  font-weight: 600;
}

.direction-summary .pending-tip {
  margin: 0.14rem 0 0;
}

.direction article {
  padding: 0.18rem 0;
  border-bottom: 1px solid #eee;
}

.direction article b,
.ai article b,
.direction-list h3 {
  font-size: 0.29rem;
  font-weight: 600;
  line-height: 1.35;
}

.direction article b,
.ai article b {
  display: block;
  color: #333;
}

.direction article strong {
  color: @color;
  font-size: 0.3rem;
}

.direction-list {
  margin-top: 0.16rem;
  padding: 0.14rem 0.16rem;
  border: 1px solid #f0f1f5;
  border-radius: 0.1rem;
  background: #fafbfc;
}

.direction-list + .direction-list {
  margin-top: 0.12rem;
}

.direction-list.deviation {
  background: #fff8f6;
  border-color: #f3e4e1;
}

.direction-list.suggestion {
  background: #f6f9fc;
  border-color: #e3ebf3;
}

.direction-list.deviation h3 {
  color: #b45309;
}

.direction-list.suggestion h3 {
  color: #2f6fad;
}

.direction-list h3 {
  margin: 0 0 0.08rem;
}

.direction-list p {
  margin: 0;
  padding: 0.1rem 0;
  color: #5f6272;
  font-size: 0.24rem;
  line-height: 1.55;
  word-break: break-word;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.direction-list p:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

h2 {
  font-size: 0.34rem;
  color: #333;
}

main section {
  padding: 0.22rem 0;
  border-bottom: 1px solid #eee;
}

main label {
  color: #8b8d9d;
  font-size: 0.25rem;
}

main p {
  margin-top: 0.08rem;
  line-height: 1.6;
  white-space: pre-wrap;
  font-size: 0.3rem;
}

.ai > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ai > header div span {
  display: block;
  margin-top: 0.05rem;
  color: #876421;
  font-size: 0.23rem;
}

.ai > header > b {
  color: #B71C1C;
  font-size: 0.45rem;
}

.composition {
  margin: 0.2rem 0;
  padding: 0.18rem;
  color: #55586a;
  background: #fff5f5;
  border-radius: 0.12rem;
  font-size: 0.25rem;
}

.fallback {
  color: #c06b28;
  font-size: 0.25rem;
}

.pending-tip {
  margin: 0.16rem 0 0.08rem;
  padding: 0.16rem 0.18rem;
  color: #876421;
  background: #fff8e8;
  border-radius: 0.12rem;
  font-size: 0.25rem;
  line-height: 1.5;
}

.refresh-ai {
  margin-left: 0.12rem;
  padding: 0.04rem 0.14rem;
  color: #B71C1C;
  border: 1px solid rgba(183, 28, 28, 0.35);
  border-radius: 0.2rem;
  background: #fff;
  font-size: 0.24rem;
}

.refresh-ai:disabled {
  opacity: 0.55;
}

.ai article {
  padding: 0.18rem 0;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #eee;
}

.ai article div {
  min-width: 0;
  flex: 1;
}

.ai article b,
.ai article span {
  display: block;
}

.ai article span {
  margin-top: 0.05rem;
  color: #777;
  font-size: 0.23rem;
  line-height: 1.45;
}

.ai article strong {
  margin-left: 0.2rem;
  color: #B71C1C;
  font-size: 0.34rem;
}

.ai > small {
  display: block;
  margin-top: 0.2rem;
  color: #999;
  font-size: 0.23rem;
  line-height: 1.5;
}

.score > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.score > div span {
  color: #B71C1C;
  font-size: 0.26rem;
}

.finance-tip {
  margin: 0.22rem 0;
  padding: 0.2rem;
  color: #686b7e;
  font-size: 0.25rem;
  line-height: 1.55;
  background: #fff5f5;
  border-radius: 0.12rem;
}

.finance-tip b {
  color: #B71C1C;
  font-size: 0.3rem;
}

.score > .finance-slider {
  display: block;
  padding: 0.18rem 0.14rem 0.3rem;
}

.slider-value {
  margin-bottom: 0.42rem;
  color: #686b7e;
  font-size: 0.28rem;
  white-space: nowrap;
}

.slider-button {
  width: 0.7rem;
  height: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #B71C1C;
  box-shadow: 0 0.04rem 0.16rem rgba(211, 47, 47, 0.35);
  font-size: 0.22rem;
  font-weight: 700;
}

.slider-marks {
  margin-top: 0.24rem;
  display: flex;
  justify-content: space-between;
  color: #989aab;
  font-size: 0.22rem;
}

.score > .finance-reason {
  display: block;
  margin-top: 0.08rem;
  padding-top: 0.28rem;
  border-top: 1px solid #eeeeF4;
}

.finance-reason > label {
  display: block;
  margin-bottom: 0.16rem;
  color: #4b4e61;
  font-size: 0.28rem;
  font-weight: 600;
  white-space: nowrap;
}

.finance-reason .van-cell {
  padding: 0.22rem 0.24rem;
  border: 1px solid #f5d0d0;
  border-radius: 0.12rem;
  background: #fffafa;
}

footer {
  position: fixed;
  z-index: 5;
  right: 0;
  bottom: 50px;
  left: 0;
  padding: 0.2rem 0.25rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.14rem;
  background: #fff;
  box-shadow: 0 -0.05rem 0.2rem rgba(0, 0, 0, 0.08);
}

.review-action {
  height: 0.82rem;
  border-radius: 0.14rem;
  font-size: 0.29rem;
  font-weight: 500;
  letter-spacing: 0;
}

.review-action.primary {
  box-shadow: 0 0.08rem 0.2rem rgba(211, 47, 47, 0.2);
}

.star-rank {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 0.3rem;
  padding-top: 0.3rem;
  border-top: 1px solid #eee;
  box-sizing: border-box;
}

.star-rank-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
  width: 100%;
  margin-bottom: 0.24rem;
  height: 0.48rem;
}

.star-rank-title span {
  flex: 0 0 auto;
  display: block;
  font-size: 0.34rem;
  color: #333;
  font-weight: 700;
  white-space: nowrap;
}

.star-rank-title em {
  flex: 0 0 auto;
  display: block;
  font-size: 0.26rem;
  color: #B71C1C;
  font-style: normal;
  white-space: nowrap;
}

.star-rank-list {
  display: flex;
  justify-content: space-between;
  flex: none;
  width: 100%;
}

.star-rank-item {
  flex: 1 1 0;
  min-width: 0;
  margin: 0 0.06rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;
}

.star-rank-item:first-child {
  margin-left: 0;
}

.star-rank-item:last-child {
  margin-right: 0;
}

.star-rank-item.dragging .star-btn {
  opacity: 0.6;
}

.star-btn {
  width: 100%;
  padding: 0.18rem 0.1rem;
  background: #fff;
  color: #333;
  border: 1px solid #e0e0e0;
  border-radius: 0.14rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.22rem;
  position: relative;
  box-shadow: 0 0.02rem 0.06rem rgba(0, 0, 0, 0.04);
}

.star-btn.empty .star-dim {
  color: #999;
}

.star-dim {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 76%;
  line-height: 1.3;
}

.star-arrow {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 0.06rem;
  border-left: 0.07rem solid transparent;
  border-right: 0.07rem solid transparent;
  border-top: 0.07rem solid #999;
  opacity: 0.8;
}

.star-order {
  margin-top: 0.16rem;
  width: 0.42rem;
  height: 0.42rem;
  line-height: 0.42rem;
  text-align: center;
  border-radius: 50%;
  border: 1px solid #ddd;
  background: #fff;
  color: #333;
  font-size: 0.23rem;
  font-weight: 500;
}

.star-rank-tip {
  flex: none;
  display: block;
  width: 100%;
  margin-top: 0.18rem;
  font-size: 0.22rem;
  color: #999;
}

.star-rank-result {
  flex: none;
  display: block;
  width: 100%;
  margin-top: 0.16rem;
  font-size: 0.26rem;
  font-weight: 600;
  color: #B71C1C;
}

.star-rank-formula {
  flex: none;
  display: block;
  width: 100%;
  margin-top: 0.08rem;
  font-size: 0.22rem;
  color: #666;
}
</style>



// WEBPACK FOOTER //
// src/views/efficiency/supervisor/Review.vue